import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { WallpaperService } from '../../services/wallpaper.service';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html'
})
export class LandingComponent implements OnInit, OnDestroy, AfterViewInit {
  private sub = new Subscription();
  cdn = 'https://content.mahattaart.com/';

  // Banner slider (static fallback — API overrides if available)
  slides: any[] = [
    { image: 'https://storage.googleapis.com/static-mahattaart-online/banner/SM_Wall-Art-Banner_03.webp', mobile_image: 'https://storage.googleapis.com/static-mahattaart-online/banner/Phone-Banner-02%20(1).webp', published: 1, button_link: '/art-ready' },
    { image: 'https://storage.googleapis.com/static-mahattaart-online/banner/SM_P2F-Banner_07%20(1).webp', mobile_image: 'https://storage.googleapis.com/static-mahattaart-online/banner/Phone-Banner-01b.webp', published: 1, button_link: '/print-frame/featured-frames' }
  ];
  currentSlide = 0;
  private slideInterval: any;

  // Widget banner
  widget: any = null;

  // Section headings from API
  headings: any[] = [];

  // Home settings data
  wallarts: any[] = [];
  wallpapers: any[] = [];
  featuredArts: any[] = [];
  collageSets: any[] = [];
  featuredFrames: any[] = [];

  // Testimonials
  testimonials: any[] = [];
  currentTestimonialPage = 0;

  // Scroll refs
  @ViewChild('wallartScroll') wallartScrollEl!: ElementRef;
  @ViewChild('wallpaperScroll') wallpaperScrollEl!: ElementRef;
  @ViewChild('featuredArtScroll') featuredArtScrollEl!: ElementRef;
  @ViewChild('collageScroll') collageScrollEl!: ElementRef;
  @ViewChild('featuredFrameScroll') featuredFrameScrollEl!: ElementRef;
  @ViewChild('testimonialScroll') testimonialScrollEl!: ElementRef;

  // Art for business / Art advisory
  artForBusiness: any = null;
  artAdvisory: any = null;

  // Why MahattaArt items
  whyItems = [
    { label: 'Wall Decor Consultation', icon: this.cdn + 'icons/icon1.png' },
    { label: 'Customize your way', icon: this.cdn + 'icons/icon2.png' },
    { label: 'Licensed Artwork', icon: this.cdn + 'icons/icon3.png' },
    { label: '200+ Framing options', icon: this.cdn + 'icons/icon4.png' },
    { label: 'Affordable price', icon: this.cdn + 'icons/icon5.png' },
    { label: 'Top Quality', icon: this.cdn + 'icons/icon6.png' },
    { label: 'Customer Support', icon: this.cdn + 'icons/icon7.png' },
    { label: 'Easy Return & Exchange', icon: this.cdn + 'icons/icon8.png' },
    { label: 'Secured Checkout', icon: this.cdn + 'icons/icon9.png' },
    { label: 'Worldwide shipping', icon: this.cdn + 'icons/icon10.png' },
  ];

  constructor(
    private router: Router,
    private wallpaperService: WallpaperService,
    private cdr: ChangeDetectorRef,
    private dataLayer: DataLayerService,
  ) {}

  ngOnInit() {
    this.startSlideshow();
    this.loadSettings();
    this.loadHomeSettings();
    this.loadTestimonials();
    this.dataLayer.pushWallpaperLandingView();
  }

  ngAfterViewInit() {
    // Setup infinite scroll after data loads (delayed to allow DOM render)
    setTimeout(() => this.setupAllCarousels(), 1500);
  }

  private setupAllCarousels() {
    [this.wallartScrollEl, this.wallpaperScrollEl, this.featuredArtScrollEl,
     this.collageScrollEl, this.featuredFrameScrollEl, this.testimonialScrollEl
    ].forEach(ref => this.setupInfiniteScroll(ref));
  }

  private setupInfiniteScroll(elRef: ElementRef): void {
    const el = elRef?.nativeElement;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    if (el.dataset.loopSetup) return;
    el.dataset.loopSetup = '1';

    // Tag original children with data-idx before cloning
    const children = Array.from(el.children) as HTMLElement[];
    children.forEach((child, i) => child.dataset.cardIdx = String(i));

    // Clone 2x for seamless loop: [original][copy1][copy2]
    for (let c = 0; c < 2; c++) {
      children.forEach(child => el.appendChild(child.cloneNode(true)));
    }

    // Start at 1st copy
    const oneSet = el.scrollWidth / 3;
    el.style.scrollBehavior = 'auto';
    el.scrollLeft = oneSet;

    let jumping = false;
    el.addEventListener('scroll', () => {
      if (jumping) return;
      requestAnimationFrame(() => {
        if (jumping) return;
        const setW = el.scrollWidth / 3;
        if (setW <= 0) return;
        if (el.scrollLeft >= setW * 2) {
          jumping = true;
          el.style.scrollBehavior = 'auto';
          el.scrollLeft -= setW;
          requestAnimationFrame(() => { el.style.scrollBehavior = 'smooth'; jumping = false; });
        } else if (el.scrollLeft < 10) {
          jumping = true;
          el.style.scrollBehavior = 'auto';
          el.scrollLeft += setW;
          requestAnimationFrame(() => { el.style.scrollBehavior = 'smooth'; jumping = false; });
        }
      });
    });

    // Drag support
    let dragging = false, startX = 0, scrollLeft = 0, dragMoved = false;
    el.style.cursor = 'grab';
    el.addEventListener('mousedown', (e: MouseEvent) => {
      dragging = true; dragMoved = false;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.scrollBehavior = 'auto';
    });
    el.addEventListener('mousemove', (e: MouseEvent) => {
      if (!dragging) return;
      e.preventDefault();
      dragMoved = true;
      el.scrollLeft = scrollLeft - (e.pageX - el.offsetLeft - startX) * 1.5;
    });
    const stopDrag = () => { if (!dragging) return; dragging = false; el.style.cursor = 'grab'; el.style.scrollBehavior = 'smooth'; };
    el.addEventListener('mouseup', stopDrag);
    el.addEventListener('mouseleave', stopDrag);

    // Click delegation — works on cloned items too
    el.addEventListener('click', (e: MouseEvent) => {
      if (dragMoved) { dragMoved = false; return; } // ignore drag-end clicks
      const card = (e.target as HTMLElement).closest('[data-card-idx]') as HTMLElement;
      if (!card) return;
      const idx = +card.dataset.cardIdx!;
      this.onCarouselCardClick(el, idx);
    });
  }

  private onCarouselCardClick(el: HTMLElement, idx: number) {
    const ref = this.getCarouselRef(el);
    if (ref === this.wallartScrollEl) {
      this.navigateToWallArt(this.wallarts[idx]);
    } else if (ref === this.wallpaperScrollEl) {
      this.navigateToWallpaper(this.wallpapers[idx]);
    } else if (ref === this.featuredArtScrollEl) {
      this.navigateToFeaturedArt(this.featuredArts[idx]);
    } else if (ref === this.collageScrollEl) {
      this.navigateToCollage(this.collageSets[idx]);
    } else if (ref === this.featuredFrameScrollEl) {
      this.navigateToFrame(this.featuredFrames[idx]);
    }
  }

  private getCarouselRef(el: HTMLElement): ElementRef | null {
    const refs = [this.wallartScrollEl, this.wallpaperScrollEl, this.featuredArtScrollEl,
                  this.collageScrollEl, this.featuredFrameScrollEl, this.testimonialScrollEl];
    return refs.find(r => r?.nativeElement === el) || null;
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    clearInterval(this.slideInterval);
  }

  // ── Data loading ──────────────────────────────────────────
  private loadSettings() {
    this.sub.add(
      this.wallpaperService.getSetting().subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          if (data?.home) {
            const home = data.home;
            // slides are static (defined in component)
            this.headings = home.heading || [];
            if (home.widgets?.length > 0 && home.widgets[0]?.published == 1) {
              this.widget = home.widgets[0];
            }
            if (home.art_for_business?.length > 0) {
              this.artForBusiness = home.art_for_business[0];
            }
            if (home.art_advisory_service?.length > 0) {
              this.artAdvisory = home.art_advisory_service[0];
            }
            this.startSlideshow();
          }
        }
      })
    );
  }

  private loadHomeSettings() {
    this.sub.add(
      this.wallpaperService.getHomeSettings().subscribe({
        next: (res: any) => {
          this.wallarts = res?.wall_arts || [];
          this.wallpapers = res?.wallpapers || [];
          this.featuredArts = res?.featured_arts || [];
          this.collageSets = res?.collage_sets || [];
          this.featuredFrames = res?.featured_frames || [];
          this.cdr.detectChanges();
          // Setup carousels after DOM renders with data
          setTimeout(() => this.setupAllCarousels(), 500);
        }
      })
    );
  }

  private loadTestimonials() {
    this.sub.add(
      this.wallpaperService.getTestimonials().subscribe({
        next: (res: any) => {
          const list = res?.data || res || [];
          this.testimonials = list.length > 0 ? list : this.fallbackTestimonials;
          this.cdr.detectChanges();
          setTimeout(() => this.setupInfiniteScroll(this.testimonialScrollEl), 500);
        },
        error: () => {
          this.testimonials = this.fallbackTestimonials;
        }
      })
    );
  }

  private fallbackTestimonials = [
    { user: { name: 'Neha Srivastava' }, review: 'They guys are awesome. Print, frame, price, everything is awesome, and I am really impressed with the quality & ...', image_1: '' },
    { user: { name: 'Krishna Sharma' }, review: 'Frames designed and organised by Mahatta Art is not only eye catching but also pocket friendly. Highly satisfied, in pro...', image_1: '' },
    { user: { name: 'Shweta' }, review: 'You gave me this collage frame in such a short time, thank you from the bottom of my heart.', image_1: '' },
    { user: { name: 'Basant Gupte' }, review: 'Professional service and amazing results', image_1: '' },
    { user: { name: 'Krishna Bharti' }, review: 'Great work !', image_1: '' },
  ];

  // ── Banner slideshow ──────────────────────────────────────
  startSlideshow() {
    if (this.slides.length <= 1) return;
    this.slideInterval = setInterval(() => {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    }, 6000);
  }

  goToSlide(i: number) {
    this.currentSlide = i;
    clearInterval(this.slideInterval);
    this.startSlideshow();
  }

  prevSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    clearInterval(this.slideInterval);
    this.startSlideshow();
  }

  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    clearInterval(this.slideInterval);
    this.startSlideshow();
  }

  // ── Scroll navigation ────────────────────────────────────
  scrollNav(ref: ElementRef, dir: 'left' | 'right') {
    if (!ref?.nativeElement) return;
    const el = ref.nativeElement;
    const scrollAmt = el.clientWidth * 0.8;
    el.scrollBy({ left: dir === 'left' ? -scrollAmt : scrollAmt, behavior: 'smooth' });
  }

  // ── Drag to scroll ──────────────────────────────────────
  private isDragging = false;
  private dragStartX = 0;
  private dragScrollLeft = 0;
  private dragRef: HTMLElement | null = null;

  onDragStart(e: MouseEvent, ref: ElementRef) {
    const el = ref?.nativeElement;
    if (!el) return;
    this.isDragging = true;
    this.dragRef = el;
    this.dragStartX = e.pageX - el.offsetLeft;
    this.dragScrollLeft = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.scrollBehavior = 'auto';
  }

  onDragMove(e: MouseEvent) {
    if (!this.isDragging || !this.dragRef) return;
    e.preventDefault();
    const x = e.pageX - this.dragRef.offsetLeft;
    const walk = (x - this.dragStartX) * 1.5;
    this.dragRef.scrollLeft = this.dragScrollLeft - walk;
  }

  onDragEnd() {
    if (!this.dragRef) return;
    this.isDragging = false;
    this.dragRef.style.cursor = 'grab';
    this.dragRef.style.scrollBehavior = 'smooth';
    this.dragRef = null;
  }

  // ── Testimonial pagination ────────────────────────────────
  get testimonialPages(): number {
    return Math.ceil(this.testimonials.length / this.testimonialsPerPage);
  }

  get testimonialsPerPage(): number {
    return 4;
  }

  get visibleTestimonials(): any[] {
    const start = this.currentTestimonialPage * this.testimonialsPerPage;
    return this.testimonials.slice(start, start + this.testimonialsPerPage);
  }

  prevTestimonials() {
    if (this.currentTestimonialPage > 0) this.currentTestimonialPage--;
  }

  nextTestimonials() {
    if (this.currentTestimonialPage < this.testimonialPages - 1) this.currentTestimonialPage++;
  }

  // ── Heading helpers ───────────────────────────────────────
  getHeading(index: number): string {
    return this.headings[index]?.title || '';
  }

  // ── Image helpers ─────────────────────────────────────────
  getWallArtImage(item: any): string {
    if (item?.image_filename) {
      return 'https://storage.googleapis.com/static-mahattaart-com/400x400/media/' + item.image_filename;
    }
    return item?.list_thumbnail || item?.thumbnail || item?.image || item?.images?.[0] || '';
  }

  getProductImage(item: any): string {
    return item?.image_2 || item?.list_thumbnail || item?.thumbnail || item?.image || item?.images?.[0] || '';
  }

  getCollageImage(item: any): string {
    return item?.image || item?.thumbnail || item?.images?.[0] || '';
  }

  getTestimonialImage(t: any): string {
    const di = t.default_image || 1;
    return t['image_' + di] || t.image_1 || t.image_2 || t.image_3 || '';
  }

  // ── Navigation ────────────────────────────────────────────
  navigateToWallArt(item?: any) {
    if (item?.image_id) {
      this.router.navigate(['/product-customization'], {
        queryParams: { product: 'wall_art', product_type: 'canvas', image_id: item.image_id }
      });
    } else {
      this.router.navigate(['/wall-art']);
    }
  }

  navigateToWallpaper(wp: any) {
    if (wp?.slug) {
      this.router.navigate(['/wallpaper', wp.slug]);
    } else if (wp?.id) {
      this.router.navigate(['/wallpaper', wp.id]);
    }
  }

  navigateToCollage(item: any) {
    if (item?.slug) {
      this.router.navigate(['/collage-set', item.slug]);
    }
  }

  navigateToFrame(item: any) {
    this.dataLayer.pushSelectPromotion({
      id: item?.id ? `FRAME_${item.id}` : 'FRAME',
      name: item?.caption || item?.title || 'Featured Frame',
      creative: 'featured_tile',
      slot: 'featured_frames'
    });
    const slug = this.toSlug(item?.caption || item?.title || item?.slug);
    if (slug) this.router.navigate(['/featured-frame', slug]);
  }

  navigateToFeaturedArt(item: any) {
    this.dataLayer.pushSelectPromotion({
      id: item?.id ? `ART_${item.id}` : 'ART',
      name: item?.caption || item?.title || 'Featured Art',
      creative: 'featured_tile',
      slot: 'featured_arts'
    });
    const slug = this.toSlug(item?.caption || item?.title || item?.slug);
    if (slug) this.router.navigate(['/featured-art', slug]);
  }

  private toSlug(text: string): string {
    if (!text) return '';
    return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  onBannerClick(slide: any) {
    // GTM: select_promotion on banner click
    const idx = this.slides.indexOf(slide);
    this.dataLayer.pushSelectPromotion({
      id: slide.id ? `BANNER_${slide.id}` : `BANNER_${idx}`,
      name: slide.title || slide.alt_text || `Hero Banner ${idx + 1}`,
      creative: 'hero_banner',
      slot: `hero_${idx + 1}`
    });
    if (slide?.button_link) {
      const link: string = slide.button_link;
      if (link.startsWith('http')) {
        try {
          const url = new URL(link);
          if (url.hostname.includes('mahattaart.com')) {
            // Same-domain URL — navigate internally so Angular routing works
            this.router.navigateByUrl(url.pathname + url.search + url.hash);
          } else {
            window.open(link, '_blank');
          }
        } catch {
          window.open(link, '_blank');
        }
      } else {
        this.router.navigateByUrl(link);
      }
    }
  }

  trackByIdx(index: number): number { return index; }
}
